'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Hash, List, Dice5 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomNumberGeneratorContent } from '../../data/random-number-generator-content';
import Link from 'next/link';

type NumberType = 'integer' | 'decimal' | 'sequence';
type SortOrder = 'none' | 'asc' | 'desc';

const generateRandomNumber = (min: number, max: number, decimalPlaces: number = 0): number => {
    const factor = Math.pow(10, decimalPlaces);
    const random = Math.random() * (max - min) + min;
    return Math.round(random * factor) / factor;
};

const generateRandomNumbers = (
    count: number,
    min: number,
    max: number,
    decimalPlaces: number,
    allowDuplicates: boolean,
    sortOrder: SortOrder = 'none'
): number[] => {
    const numbers: number[] = [];
    const range = max - min + 1;

    // For integer sequences without duplicates, we can optimize
    if (!allowDuplicates && decimalPlaces === 0 && count <= range) {
        // Create a set of all possible numbers in range
        const possibleNumbers = Array.from({ length: range }, (_, i) => min + i);

        // Shuffle and take first 'count' numbers
        for (let i = possibleNumbers.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [possibleNumbers[i], possibleNumbers[j]] = [possibleNumbers[j], possibleNumbers[i]];
        }

        numbers.push(...possibleNumbers.slice(0, count));
    } else {
        // For decimals or when duplicates are allowed
        const seen = new Set<number>();

        while (numbers.length < count) {
            const num = generateRandomNumber(min, max, decimalPlaces);

            if (!allowDuplicates) {
                const key = decimalPlaces > 0 ? num.toFixed(decimalPlaces) : num;
                if (seen.has(Number(key))) continue;
                seen.add(Number(key));
            }

            numbers.push(num);

            // Safety check to prevent infinite loops
            if (!allowDuplicates && numbers.length >= Math.min(count, max - min + 1)) {
                break;
            }
        }
    }

    // Apply sorting if needed
    if (sortOrder === 'asc') {
        numbers.sort((a, b) => a - b);
    } else if (sortOrder === 'desc') {
        numbers.sort((a, b) => b - a);
    }

    return numbers;
};

export default function RandomNumberGenerator() {
    const [numberType, setNumberType] = useState<NumberType>('integer');
    const [min, setMin] = useState<number>(1);
    const [max, setMax] = useState<number>(100);
    const [count, setCount] = useState<number>(1);
    const [decimalPlaces, setDecimalPlaces] = useState<number>(0);
    const [allowDuplicates, setAllowDuplicates] = useState<boolean>(false);
    const [sortOrder, setSortOrder] = useState<SortOrder>('none');
    const [results, setResults] = useState<number[]>([]);
    const [copied, setCopied] = useState<boolean>(false);
    const { toast } = useToast();

    const generateNumbers = useCallback(() => {
        try {
            const newResults = generateRandomNumbers(
                count,
                min,
                max,
                decimalPlaces,
                allowDuplicates,
                sortOrder
            );

            setResults(newResults);
        } catch (error) {
            console.error('Error generating numbers:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate numbers. Please check your parameters.',
                variant: 'destructive',
            });
        }
    }, [min, max, count, decimalPlaces, allowDuplicates, sortOrder, toast]);

    const handleCopy = useCallback(() => {
        if (results.length === 0) return;

        const textToCopy = results.join(', ');
        navigator.clipboard.writeText(textToCopy);

        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Numbers have been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [results, toast]);

    const handleNumberTypeChange = (type: string) => {
        const newType = type as NumberType;
        setNumberType(newType);

        // Adjust decimal places based on number type
        if (newType === 'decimal' && decimalPlaces === 0) {
            setDecimalPlaces(2);
        } else if (newType !== 'decimal' && decimalPlaces > 0) {
            setDecimalPlaces(0);
        }
    };

    // Generate initial numbers on mount
    useEffect(() => {
        generateNumbers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update results when dependencies change
    useEffect(() => {
        generateNumbers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberType, min, max, count, decimalPlaces, allowDuplicates, sortOrder]);

    const formatNumber = (num: number): string => {
        if (numberType === 'integer') {
            return Math.floor(num).toString();
        }
        return num.toFixed(decimalPlaces);
    };

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random Number Generator">
                Generate random numbers with our free online tool. Create integers, decimals, and custom ranges for statistics, gaming, and development.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Configuration</CardTitle>
                            <CardDescription>Customize your random number generation</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <Label className="block mb-2">Number Type</Label>
                                    <Tabs
                                        value={numberType}
                                        onValueChange={handleNumberTypeChange}
                                        className="w-full"
                                    >
                                        <TabsList className="grid w-full grid-cols-3">
                                            <TabsTrigger value="integer">
                                                <Hash className="h-4 w-4 mr-2" />
                                                Integer
                                            </TabsTrigger>
                                            <TabsTrigger value="decimal">
                                                <Dice5 className="h-4 w-4 mr-2" />
                                                Decimal
                                            </TabsTrigger>
                                            <TabsTrigger value="sequence">
                                                <List className="h-4 w-4 mr-2" />
                                                Sequence
                                            </TabsTrigger>
                                        </TabsList>
                                    </Tabs>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="min">Minimum Value</Label>
                                        <Input
                                            id="min"
                                            type="number"
                                            value={min}
                                            onChange={(e) => setMin(Number(e.target.value))}
                                            step={numberType === 'integer' ? '1' : 'any'}
                                            min={-1000000}
                                            max={max - 1}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="max">Maximum Value</Label>
                                        <Input
                                            id="max"
                                            type="number"
                                            value={max}
                                            onChange={(e) => setMax(Number(e.target.value))}
                                            step={numberType === 'integer' ? '1' : 'any'}
                                            min={min + 1}
                                            max={1000000}
                                        />
                                    </div>
                                </div>

                                {numberType === 'decimal' && (
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <Label htmlFor="decimal-places">Decimal Places: {decimalPlaces}</Label>
                                        </div>
                                        <Slider
                                            id="decimal-places"
                                            value={[decimalPlaces]}
                                            onValueChange={([value]) => setDecimalPlaces(value)}
                                            min={0}
                                            max={10}
                                            step={1}
                                            className="py-4"
                                        />
                                    </div>
                                )}

                                <div>
                                    <div className="flex justify-between mb-2">
                                        <Label htmlFor="count">Count: {count}</Label>
                                        <span className="text-sm text-muted-foreground">
                                            {count} {count === 1 ? 'number' : 'numbers'}
                                        </span>
                                    </div>
                                    <Slider
                                        id="count"
                                        value={[count]}
                                        onValueChange={([value]) => setCount(value)}
                                        min={1}
                                        max={1000}
                                        step={1}
                                        className="py-4"
                                    />
                                </div>

                                <div className="space-y-4 pt-2">
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-1">
                                            <Label>Allow Duplicates</Label>
                                            <p className="text-sm text-muted-foreground">
                                                {allowDuplicates ? 'Numbers may repeat' : 'All numbers will be unique'}
                                            </p>
                                        </div>
                                        <Switch
                                            checked={allowDuplicates}
                                            onCheckedChange={setAllowDuplicates}
                                            disabled={numberType === 'sequence'}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Sort Order</Label>
                                        <div className="flex gap-2">
                                            <Button
                                                variant={sortOrder === 'none' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setSortOrder('none')}
                                                className="flex-1"
                                            >
                                                None
                                            </Button>
                                            <Button
                                                variant={sortOrder === 'asc' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setSortOrder('asc')}
                                                className="flex-1"
                                            >
                                                Ascending
                                            </Button>
                                            <Button
                                                variant={sortOrder === 'desc' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setSortOrder('desc')}
                                                className="flex-1"
                                            >
                                                Descending
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2">
                                <Button
                                    onClick={generateNumbers}
                                    className="w-full"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate New Numbers
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Results</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <div className="text-sm text-muted-foreground">
                                        {results.length} {results.length === 1 ? 'number' : 'numbers'}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={results.length === 0}
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4 mr-2" />
                                        ) : (
                                            <Copy className="h-4 w-4 mr-2" />
                                        )}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {results.length > 0 ? (
                                <div className="bg-muted/50 p-4 rounded-md">
                                    <div className="font-mono text-sm break-words">
                                        {results.map((num, index) => (
                                            <span key={index} className="inline-block mr-2 mb-2">
                                                {formatNumber(num)}
                                                {index < results.length - 1 && ','}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    No numbers generated yet. Adjust the settings and click "Generate".
                                </div>
                            )}

                            {results.length > 0 && (
                                <div className="mt-4 text-sm text-muted-foreground">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p>Min: <span className="font-mono">{formatNumber(Math.min(...results))}</span></p>
                                            <p>Max: <span className="font-mono">{formatNumber(Math.max(...results))}</span></p>
                                        </div>
                                        <div>
                                            <p>Sum: <span className="font-mono">{formatNumber(results.reduce((a, b) => a + b, 0))}</span></p>
                                            <p>Avg: <span className="font-mono">{formatNumber(results.reduce((a, b) => a + b, 0) / results.length)}</span></p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool uses cryptographically secure random number generation.
                        All processing happens in your browser - no data is sent to any server.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomNumberGeneratorContent.title}
                description={randomNumberGeneratorContent.description}
                content={randomNumberGeneratorContent.content}
                faqs={randomNumberGeneratorContent.faqs}
                relatedTools={randomNumberGeneratorContent.relatedTools}
            />
        </div>
    );
}
